import { useState } from "react";
import toast from "react-hot-toast";
import { SEO } from "../../components/SEO";
import { API } from "../../utils/axios";

export default function Contact() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneNoError, setphoneNoError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhone = (phone: string) => {
    const regex = /^[1-9]\d{9}$/;
    return regex.test(phone);
  };

  const handleEmailChange = (e: any) => {
    const value = e.target.value;
    setEmail(value);

    if (value === "") {
      setEmailError("Email is required");
    } else if (!validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handlePhoneNoChange = (e: any) => {
    const value = e.target.value;
    setPhoneNo(value);

    if (value === "") {
      setphoneNoError("Phone number is required");
    } else if (!validatePhone(value)) {
      setphoneNoError("Please enter a valid 10-digit phone number");
    } else {
      setphoneNoError("");
    }
  };

  const sendMessage = async () => {
    if (isLoading) {
      return;
    }
    if (!fullName || !email || !message) {
      toast.error("Please fill required fields");
      return;
    }
    setIsLoading(true);

    try {
      const res = await API.post('/contact',{
          fullName,
          email,
          message,
          phoneNo,
        });

      if (res.data.success) {
        setIsSubmitted(true)
        setFullName("");
        setEmail("");
        setMessage("");
        setPhoneNo("");
      } else {
        toast.error(res.data.msg);
      }
    } catch (error) {
      toast.error("Error in sending Information");
      console.error("Error in sending Information", error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1500);
    }
  };

  return (
    <main>
      <SEO
        title="Contact Last Minute Wellness | Holistic Healing in Canada "
        description="Reach out for support or holistic healing guidance. We’re here to help Canadians book trusted wellness services anytime, anywhere."
        url="https://lastminutewellness.ca/contact"
        image="https://lastminutewellness.ca/about-banner.png"
      />

      <section aria-label="Contact Form Section">
        <div className="relative bg-gradient-to-br from-gray-50 via-white to-gray-100 py-8 px-4 sm:px-6 lg:px-12">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center lg:items-start justify-between gap-10">
            {/* Left Section - Info */}
            <div className="text-center my-auto space-y-4 lg:text-left lg:w-1/2 text-[#333333]">
              <h1 className="text-3xl sm:text-6xl md:text-6xl lg:text-6xl font-bold mb-6">
                <span>Contact Us</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl max-w-xl mx-auto lg:mx-0">
                Ready to book your experience, or want to share your thoughts
                with us? Drop us a message — we value your feedback just as much
                as your bookings.
              </p>
              <p>You can also call us at 403-519-4004.</p>
            </div>

            {/* Right Section - Form */}
            <div className="w-full lg:w-1/2 bg-white shadow-xl rounded-2xl py-4 px-6 sm:p-8 lg:p-10 border border-gray-100">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">
                Send us your Thoughts or Feedback
              </h3>

              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center text-center py-12 px-6 space-y-4">

                  {/* Title */}
                  <h3 className="text-2xl font-semibold text-gray-900">
                    Message Sent Successfully!
                  </h3>

                  {/* Message */}
                  <p className="text-gray-600 max-w-md">
                    Thank you for contacting us. We’ve received your message and
                    will get back to you{" "}
                    <span className="font-medium text-gray-800">
                      within 24 hours
                    </span>
                    .
                  </p>
                </div>
              ) : (
                <form className="space-y-6">
                  {/* Name + Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Enter your full name"
                        className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number (optional)
                      </label>
                      <input
                        type="text"
                        value={phoneNo}
                        onChange={handlePhoneNoChange}
                        placeholder="Enter phone number"
                        className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      />
                      {phoneNoError && (
                        <p className="text-red-600 text-sm mt-1">
                          {phoneNoError}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Email <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="Enter your email"
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                    {emailError && (
                      <p className="text-red-500 text-sm mt-1">{emailError}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      rows={4}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Share your thoughts, questions or valuable feedback..."
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>

                  {/* Button */}
                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={isLoading}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg px-6 py-3 transition flex items-center justify-center disabled:opacity-50"
                  >
                    {isLoading ? (
                      <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
